import React, { PureComponent, Fragment, createRef } from 'react'
import PropTypes from 'prop-types'
import { t } from 'i18n'

import Notification from './Notification'
import { Loader } from 'Components/Base'
import BellNotificationEmpty from '../../../assets/images/BellNotificationEmpty.svg'

const i18nOpts = { scope: 'notifications' }

class NotificationList extends PureComponent {
  constructor(props) {
    super(props)
    this.sentinelRef = createRef()
    this.observer = null
  }

  componentDidMount() {
    this.setupObserver()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.hasMore !== this.props.hasMore) {
      this.setupObserver()
    }
  }

  componentWillUnmount() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  setupObserver() {
    if (this.observer) {
      this.observer.disconnect()
    }

    if (!this.props.hasMore) return

    this.observer = new IntersectionObserver(
      (entries) => {
        const { hasMore, isLoadingMore, onLoadMore } = this.props
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          onLoadMore()
        }
      },
      { threshold: 0.1 }
    )

    if (this.sentinelRef.current) {
      this.observer.observe(this.sentinelRef.current)
    }
  }

  get groupLabels() {
    return [...new Set(this.props.notifications.map(({ groupLabel }) => groupLabel))]
  }

  notificationsByGroupLabel(groupLabel) {
    return this.props.notifications.filter((notification) => notification.groupLabel === groupLabel)
  }

  renderNotification({ id, link, body, timestamp, isUnread }) {
    return <Notification key={id} id={id} link={link} body={body} timestamp={timestamp} isUnread={isUnread} />
  }

  renderList() {
    let lastGroupLabel = null;

    return (
      <ul className="notification-tray__list">
        {this.props.notifications.map((notification) => {
          const showGroupHeader = notification.groupLabel !== lastGroupLabel;
          if (showGroupHeader) {
            lastGroupLabel = notification.groupLabel;
          }

          return (
            <Fragment key={notification.id}>
              {showGroupHeader && (
                <div className="notification-tray__group-title">
                  {notification.groupLabel}
                </div>
              )}
              {this.renderNotification(notification)}
            </Fragment>
          );
        })}
      </ul>
    );
  }

  renderEmpty() {
    return (
      <div className="notification-tray__empty">
        <img src={BellNotificationEmpty} alt="" className="notification-tray__empty-icon" />
        <div className="notification-tray__empty-title">{t('empty.title', i18nOpts)}</div>
        <p>{t('empty.message', i18nOpts)}</p>
      </div>
    )
  }

  renderLoadingMore() {
    if (!this.props.isLoadingMore) return null
    return (
      <div className="notification-tray__loading-more">
        <Loader />
      </div>
    )
  }

  renderEndOfList() {
    const { activeTab, hasMore, notifications } = this.props
    if (activeTab !== 'all' || hasMore || !notifications.length) return null

    return (
      <div className="notification-tray__end-of-list">
        <img src={BellNotificationEmpty} alt="" className="notification-tray__end-of-list-icon" />
        <p>{t('end_of_list', i18nOpts)}</p>
      </div>
    )
  }

  render() {
    return (
      <div className="notification-tray">
        {this.props.notifications.length ? this.renderList() : this.renderEmpty()}
        {this.renderLoadingMore()}
        {this.props.hasMore && <div ref={this.sentinelRef} className="notification-tray__sentinel" />}
        {this.renderEndOfList()}
      </div>
    )
  }
}

const notificationShape = PropTypes.shape({
  body: PropTypes.string.isRequired,
  timestamp: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
  link: PropTypes.string.isRequired,
  groupLabel: PropTypes.string.isRequired,
  isUnread: PropTypes.bool,
})

NotificationList.propTypes = {
  notifications: PropTypes.arrayOf(notificationShape).isRequired,
  activeTab: PropTypes.string,
  hasMore: PropTypes.bool,
  isLoadingMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
}

NotificationList.defaultProps = {
  activeTab: 'unread',
  hasMore: false,
  isLoadingMore: false,
  onLoadMore: () => {},
}

export default NotificationList
